import { useState, useEffect } from "react";
import axios from "axios";
import { Phone, WhatsappLogo, MapPin, Wrench, Clock, ArrowClockwise, MagnifyingGlass, GoogleLogo, Globe, Link, Tag } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "inspection": "AC Inspection", "jet-service": "AC Jet Service", "cleaning": "AC Cleaning",
  "foam-jet": "AC Foam Jet", "installation": "AC Installation", "removal": "AC Removal",
  "gas-charging": "AC Gas Charging", "window-mesh": "Window Mesh", "single-door": "Single Door Mesh",
  "double-door": "Double Door Mesh", "bathroom-mesh": "Bathroom Mesh", "balcony-mesh": "Balcony Mesh",
  "full-home": "Full Home Mesh", "not-spinning": "WM: Not Spinning", "water-leakage": "WM: Water Leakage",
  "drainage-issue": "WM: Drainage Issue", "pcb-issue": "WM: PCB Issue", "motor-failure": "WM: Motor Failure",
  "drum-noise": "WM: Drum Noise", "power-problem": "WM: Power Problem", "door-lock": "WM: Door Lock",
  "other": "Other", "fridge-not-cooling": "Fridge: Not Cooling", "fridge-over-freezing": "Fridge: Over Freezing",
  "fridge-water-leakage": "Fridge: Water Leakage", "fridge-loud-noise": "Fridge: Loud Noise",
  "fridge-pcb---sensor-issue": "Fridge: PCB/Sensor", "fridge-compressor-problem": "Fridge: Compressor",
  "fridge-power-issue": "Fridge: Power Issue", "fridge-door-seal-problem": "Fridge: Door Seal",
  "fridge-other": "Fridge: Other", "sbs-not-cooling": "SBS: Not Cooling",
  "sbs-freezer-not-freezing": "SBS: Freezer", "sbs-water-leakage": "SBS: Water Leak",
  "sbs-ice-maker-problem": "SBS: Ice Maker", "sbs-display-panel-issue": "SBS: Display",
  "sbs-compressor-problem": "SBS: Compressor", "sbs-other": "SBS: Other",
};

const STATUSES = ["New", "Contacted", "Qualified", "Not Qualified", "Converted"];

const STATUS_COLORS = {
  "New": "bg-blue-100 text-blue-700",
  "Contacted": "bg-yellow-100 text-yellow-700",
  "Qualified": "bg-emerald-100 text-emerald-700",
  "Not Qualified": "bg-red-100 text-red-700",
  "Converted": "bg-purple-100 text-purple-700",
};

const SOURCE_ICONS = {
  "Google Ads": GoogleLogo,
  "Organic": Globe,
  "Direct": Link,
  "Other": Tag,
};

function getServiceCategory(service) {
  if (!service) return "other";
  if (service.startsWith("fridge") || service.startsWith("sbs-")) return "fridge";
  if (["not-spinning", "water-leakage", "drainage-issue", "pcb-issue", "motor-failure", "drum-noise", "power-problem", "door-lock"].includes(service)) return "washing-machine";
  if (["window-mesh", "single-door", "double-door", "bathroom-mesh", "balcony-mesh", "full-home"].includes(service)) return "mosquito-mesh";
  return "ac-service";
}

function getCategoryColor(cat) {
  switch (cat) {
    case "ac-service": return "bg-red-100 text-red-700";
    case "washing-machine": return "bg-blue-100 text-blue-700";
    case "mosquito-mesh": return "bg-orange-100 text-orange-700";
    case "fridge": return "bg-cyan-100 text-cyan-700";
    default: return "bg-slate-100 text-slate-700";
  }
}

function getCategoryLabel(cat) {
  switch (cat) {
    case "ac-service": return "AC";
    case "washing-machine": return "WM";
    case "mosquito-mesh": return "Mesh";
    case "fridge": return "Fridge";
    default: return "Other";
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function StatusBadge({ status, leadId, onUpdate }) {
  const [open, setOpen] = useState(false);
  const color = STATUS_COLORS[status] || STATUS_COLORS["New"];

  const handleChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status: newStatus });
      onUpdate(leadId, newStatus);
    } catch (e) { console.error("Status update failed", e); }
    setOpen(false);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className={`text-[10px] font-bold px-2.5 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${color}`}>
        {status || "New"}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1 min-w-[140px]">
          {STATUSES.map(s => (
            <button key={s} onClick={() => handleChange(s)}
              className={`w-full text-left px-3 py-1.5 text-xs font-medium hover:bg-slate-50 transition-colors ${s === status ? "font-bold text-[#FF6B00]" : "text-slate-600"}`}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;

  const fetchLeads = async (pageNum = 0) => {
    setLoading(true);
    try {
      const [leadsRes, countRes] = await Promise.all([
        axios.get(`${API}/leads?limit=${PAGE_SIZE}&skip=${pageNum * PAGE_SIZE}`),
        axios.get(`${API}/leads/count`)
      ]);
      setLeads(leadsRes.data);
      setTotalCount(countRes.data.count);
      setPage(pageNum);
    } catch (e) { console.error("Failed to fetch leads", e); }
    setLoading(false);
  };

  useEffect(() => { fetchLeads(0); }, []);

  const handleStatusUpdate = (leadId, newStatus) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
  };

  const filtered = leads.filter(l => {
    const cat = getServiceCategory(l.service);
    const matchCat = filter === "all" || cat === filter;
    const matchStatus = statusFilter === "all" || (l.status || "New") === statusFilter;
    const matchSearch = !search ||
      l.name?.toLowerCase().includes(search.toLowerCase()) ||
      l.phone?.includes(search) ||
      l.area?.toLowerCase().includes(search.toLowerCase()) ||
      l.gclid?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchStatus && matchSearch;
  });

  const counts = {
    all: leads.length,
    "ac-service": leads.filter(l => getServiceCategory(l.service) === "ac-service").length,
    "washing-machine": leads.filter(l => getServiceCategory(l.service) === "washing-machine").length,
    "mosquito-mesh": leads.filter(l => getServiceCategory(l.service) === "mosquito-mesh").length,
    "fridge": leads.filter(l => getServiceCategory(l.service) === "fridge").length,
  };

  const googleAdsCount = leads.filter(l => l.gclid).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Poppins']">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <div>
            <h1 className="font-bold text-[#0F172A] text-lg">Lead Dashboard</h1>
            <p className="text-xs text-slate-400">Home Tech Repair Bangalore</p>
          </div>
          <button onClick={() => fetchLeads(page)} data-testid="refresh-btn"
            className="bg-[#FF6B00] text-white hover:bg-[#E55F00] font-semibold text-sm px-4 py-2 rounded-lg transition-all flex items-center gap-1.5">
            <ArrowClockwise size={16} weight="bold" /> Refresh
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Total", count: totalCount, color: "bg-slate-900 text-white", key: "all" },
            { label: "AC", count: counts["ac-service"], color: "bg-red-50 text-red-700 border border-red-100", key: "ac-service" },
            { label: "WM", count: counts["washing-machine"], color: "bg-blue-50 text-blue-700 border border-blue-100", key: "washing-machine" },
            { label: "Mesh", count: counts["mosquito-mesh"], color: "bg-orange-50 text-orange-700 border border-orange-100", key: "mosquito-mesh" },
            { label: "Fridge", count: counts.fridge, color: "bg-cyan-50 text-cyan-700 border border-cyan-100", key: "fridge" },
            { label: "Google Ads", count: googleAdsCount, color: "bg-green-50 text-green-700 border border-green-100", key: "gads" },
          ].map(s => (
            <button key={s.key} onClick={() => s.key !== "gads" && setFilter(s.key)} data-testid={`filter-${s.key}`}
              className={`rounded-2xl p-3 text-left transition-all hover:shadow-md ${s.color} ${filter === s.key ? "ring-2 ring-[#FF6B00] ring-offset-2" : ""}`}>
              <p className="text-xl font-extrabold">{s.count}</p>
              <p className="text-[10px] font-medium opacity-70">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Search + Status Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="search-input" type="text" placeholder="Search name, phone, area, or GCLID..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/10 outline-none transition-all" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter"
            className="bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-[#FF6B00] outline-none">
            <option value="all">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Leads */}
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading leads...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400 text-lg mb-2">No leads found</p>
            <p className="text-slate-300 text-sm">Leads will appear here when customers submit the booking form</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {filtered.map((lead, i) => {
                const cat = getServiceCategory(lead.service);
                const SourceIcon = SOURCE_ICONS[lead.source] || Tag;
                return (
                  <div key={lead.id || i} data-testid={`lead-card-${i}`}
                    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold text-[#0F172A] text-base">{lead.name}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={10} weight="fill" /> {lead.area}</p>
                      </div>
                      <StatusBadge status={lead.status || "New"} leadId={lead.id} onUpdate={handleStatusUpdate} />
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getCategoryColor(cat)}`}>{getCategoryLabel(cat)}</span>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1"><SourceIcon size={10} weight="fill" /> {lead.source || "Direct"}</span>
                      {lead.gclid && <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-mono">GCLID</span>}
                    </div>
                    <p className="text-xs text-slate-500 mb-3"><Wrench size={12} weight="bold" className="inline mr-1" />{SERVICE_LABELS[lead.service] || lead.service}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <a href={`tel:${lead.phone}`} className="bg-[#FF6B00] text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
                          <Phone size={12} weight="fill" /> {lead.phone}
                        </a>
                        <a href={`https://wa.me/91${lead.phone}`} target="_blank" rel="noopener noreferrer"
                          className="bg-[#22C55E] text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
                          <WhatsappLogo size={12} weight="fill" /> WA
                        </a>
                      </div>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock size={10} /> {formatDate(lead.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Lead ID</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Name</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Phone</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Area</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Service</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Source</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">GCLID</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Status</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Time</th>
                    <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, i) => {
                    const cat = getServiceCategory(lead.service);
                    const SourceIcon = SOURCE_ICONS[lead.source] || Tag;
                    return (
                      <tr key={lead.id || i} data-testid={`lead-row-${i}`} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3 text-[10px] text-slate-400 font-mono">{lead.id?.slice(0, 8)}...</td>
                        <td className="px-4 py-3"><p className="font-semibold text-sm text-[#0F172A]">{lead.name}</p></td>
                        <td className="px-4 py-3"><a href={`tel:${lead.phone}`} className="text-sm font-medium text-[#FF6B00] hover:underline">{lead.phone}</a></td>
                        <td className="px-4 py-3 text-sm text-slate-500">{lead.area}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getCategoryColor(cat)}`}>{SERVICE_LABELS[lead.service] || lead.service}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500 flex items-center gap-1"><SourceIcon size={12} weight="fill" className={lead.source === "Google Ads" ? "text-green-600" : "text-slate-400"} /> {lead.source || "Direct"}</span>
                        </td>
                        <td className="px-4 py-3">
                          {lead.gclid ? (
                            <span className="text-[10px] bg-green-50 text-green-700 px-2 py-1 rounded font-mono" title={lead.gclid}>{lead.gclid.slice(0, 12)}...</span>
                          ) : (
                            <span className="text-[10px] text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={lead.status || "New"} leadId={lead.id} onUpdate={handleStatusUpdate} />
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">{formatDate(lead.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5">
                            <a href={`tel:${lead.phone}`} className="bg-[#FF6B00] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#E55F00]">
                              <Phone size={10} weight="fill" /> Call
                            </a>
                            <a href={`https://wa.me/91${lead.phone}`} target="_blank" rel="noopener noreferrer"
                              className="bg-[#22C55E] text-white text-[10px] font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#16A34A]">
                              <WhatsappLogo size={10} weight="fill" /> WA
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-slate-400 text-center mt-4">
              Showing {filtered.length} of {totalCount} leads (Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))})
            </p>
            {totalCount > PAGE_SIZE && (
              <div className="flex justify-center gap-2 mt-4">
                <button onClick={() => fetchLeads(page - 1)} disabled={page === 0}
                  className="bg-white border border-slate-200 text-slate-600 font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-30 hover:border-[#FF6B00] transition-all">
                  Previous
                </button>
                <button onClick={() => fetchLeads(page + 1)} disabled={(page + 1) * PAGE_SIZE >= totalCount}
                  className="bg-[#FF6B00] text-white font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-30 hover:bg-[#E55F00] transition-all">
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
